import React, { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import BaseButton from "../../../BaseComponents/BaseButton";
import {
  FileText,
  Save,
  Info,
  ArrowLeft,
  Eye,
  Briefcase,
  UserCheck,
  BookOpen,
  Award,
  FileMinus,
} from "react-feather";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { checkStatusCodeSuccess } from "../../../Constant/common";
import { fileUploadApi } from "../../../Api/common";
import { BaseStaffURL } from "../../../Api/Service";
import logoLight from "../../../assets/images/logo/white-based-logo-long.svg";
import {
  DEFAULT_OFFER_TEMPLATE,
  DEFAULT_JOINING_TEMPLATE,
  DEFAULT_TRAINING_TEMPLATE,
  DEFAULT_EXPERIENCE_TEMPLATE,
  DEFAULT_RELIEVING_TEMPLATE,
} from "../../../utils/letterUtils";
import { logError } from "../../../utils/logger";

const getImageUrl = (path) => {
  if (!path) return "";
  if (path.startsWith("data:") || path.startsWith("http")) return path;

  const baseUrl = BaseStaffURL || "";
  let relativePath = path;
  if (baseUrl.endsWith("staff/") && relativePath.startsWith("staff/")) {
    relativePath = relativePath.substring("staff/".length);
  }
  return `${baseUrl}${relativePath}`;
};
import { getLetterConfigApi, updateLetterConfigApi } from "../../../Api/letterSettingsApi";
import { usePageLoader } from "../../../_helper/loading/LoadingContext";

const resolvePlaceholders = (template, orgName, centerName) => {
  if (!template) return "";

  const replacements = {
    name: "Abhishek Sharma",
    designation: "Senior Executive HR",
    joining_date: "01-07-2024",
    monthly_salary: "45,000",
    yearly_salary: "5,40,000",
    relieving_date: "15-06-2026",
    letter_date: "10-06-2026",
    organization_name: orgName || "Fusion HR Solutions Pvt. Ltd.",
    center_name: centerName || "Noida Office",
  };

  let resolved = template;
  Object.keys(replacements).forEach((key) => {
    resolved = resolved.replaceAll(`{${key}}`, replacements[key]);
  });
  return resolved;
};

const placeholders = [
  { tag: "{name}", desc: "Staff's Full Name" },
  { tag: "{designation}", desc: "Exit / Joining Position Designation" },
  { tag: "{joining_date}", desc: "Staff's Date of Joining (DD-MM-YYYY)" },
  { tag: "{monthly_salary}", desc: "Monthly gross salary (INR)" },
  { tag: "{yearly_salary}", desc: "Yearly gross salary (INR)" },
  { tag: "{relieving_date}", desc: "Relieving / Last Working Date (Experience Letter only)" },
  { tag: "{letter_date}", desc: "Header print date (DD-MM-YYYY)" },
  { tag: "{organization_name}", desc: "Name of the Company/Organization" },
  { tag: "{center_name}", desc: "Primary center/office branch name" },
];

const compressImage = (file, maxWidth, maxHeight, callback) => {
  const reader = new FileReader();
  reader.onload = (e) => {
    const img = new Image();
    img.onload = () => {
      let width = img.width;
      let height = img.height;

      // Keep aspect ratio
      if (width > maxWidth) {
        height = Math.round((height * maxWidth) / width);
        width = maxWidth;
      }
      if (height > maxHeight) {
        width = Math.round((width * maxHeight) / height);
        height = maxHeight;
      }

      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, width, height);

      try {
        const dataUrl = canvas.toDataURL("image/png");
        callback(null, dataUrl);
      } catch (err) {
        callback(err);
      }
    };
    img.onerror = (err) => {
      callback(err);
    };
    img.src = e.target.result;
  };
  reader.onerror = (err) => {
    callback(err);
  };
  reader.readAsDataURL(file);
};

const LetterTemplates = () => {
  const { setLoading } = usePageLoader();
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("offer");
  const [templates, setTemplates] = useState({
    offer: DEFAULT_OFFER_TEMPLATE,
    joining: DEFAULT_JOINING_TEMPLATE,
    training: DEFAULT_TRAINING_TEMPLATE,
    experience: DEFAULT_EXPERIENCE_TEMPLATE,
    relieving: DEFAULT_RELIEVING_TEMPLATE,
  });

  const [includeLogo, setIncludeLogo] = useState(true);
  const [logoPath, setLogoPath] = useState("");
  const [logoBase64, setLogoBase64] = useState("");
  const [logoWidth, setLogoWidth] = useState("120");
  const [logoAlign, setLogoAlign] = useState("left");
  const [uploadingLogo, setUploadingLogo] = useState(false);

  const [includeSignature, setIncludeSignature] = useState(false);
  const [signatoryName, setSignatoryName] = useState("Authorized Signatory");
  const [signatoryTitle, setSignatoryTitle] = useState("Authorized Signatory");
  const [signaturePath, setSignaturePath] = useState("");
  const [signatureBase64, setSignatureBase64] = useState("");
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    getLetterConfigApi()
      .then((resp) => {
        if (checkStatusCodeSuccess(resp?.statusCode)) {
          const { settings, templates: fetchedTemplates } = resp.data;
          if (fetchedTemplates) {
            setTemplates((prev) => {
              const merged = { ...prev, ...fetchedTemplates };
              const keys = Object.keys(fetchedTemplates);
              if (keys.length > 0) {
                setActiveTab((prevActive) => (keys.includes(prevActive) ? prevActive : keys[0]));
              }
              return merged;
            });
          }
          if (settings) {
            setIncludeLogo(settings.include_logo);
            setLogoPath(settings.logo_path || "");
            setLogoWidth(settings.logo_width ? String(settings.logo_width) : "120");
            setLogoAlign(settings.logo_align || "left");
            setIncludeSignature(settings.include_signatory_signature);
            setSignatoryName(settings.signatory_name || "Authorized Signatory");
            setSignatoryTitle(settings.signatory_title || "Authorized Signatory");
            setSignaturePath(settings.signature_path || "");
          }
        }
      })
      .catch((err) => {
        logError("Failed to load letter settings", err);
        toast.error("Failed to load letter settings.");
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  const quillRef = useRef(null);
  const [editorContainer, setEditorContainer] = useState(null);
  const setQuillRef = (el) => {
    quillRef.current = el;
    if (el) {
      const editor = el.getEditor();
      if (editor && editor.container) {
        setEditorContainer(editor.container);
      }
    } else {
      setEditorContainer(null);
    }
  };
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestionCoords, setSuggestionCoords] = useState({ left: 0, top: 0 });
  const [filteredPlaceholders, setFilteredPlaceholders] = useState([]);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(0);
  const [suggestionTriggerIndex, setSuggestionTriggerIndex] = useState(-1);

  useEffect(() => {
    const handleOutsideClick = () => {
      if (showSuggestions) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [showSuggestions]);

  useEffect(() => {
    if (showSuggestions) {
      const activeEl = document.querySelector(".placeholder-suggestions-dropdown .active-item");
      if (activeEl) {
        activeEl.scrollIntoView({ block: "nearest" });
      }
    }
  }, [activeSuggestionIndex, showSuggestions]);

  const checkSuggestions = (quill, range) => {
    if (!range) {
      setShowSuggestions(false);
      return;
    }

    const cursorIndex = range.index;
    const textBefore = quill.getText(0, cursorIndex);
    const lastBrace = textBefore.lastIndexOf("{");

    if (lastBrace !== -1) {
      const textAfterBrace = textBefore.substring(lastBrace + 1);
      const hasSpaceOrNewline = /\s/.test(textAfterBrace);

      if (!hasSpaceOrNewline) {
        const query = textAfterBrace.toLowerCase();
        const matches = placeholders.filter(
          (p) => p.tag.toLowerCase().includes(query) || p.desc.toLowerCase().includes(query),
        );

        if (matches.length > 0) {
          const bounds = quill.getBounds(lastBrace);
          const editorContainer = quill.container;
          const scrollContainer = editorContainer.querySelector(".ql-editor");
          const scrollTop = scrollContainer ? scrollContainer.scrollTop : 0;
          const scrollLeft = scrollContainer ? scrollContainer.scrollLeft : 0;

          setSuggestionCoords({
            left: bounds.left - scrollLeft,
            top: bounds.bottom - scrollTop + 2,
          });
          setFilteredPlaceholders(matches);
          setShowSuggestions(true);
          setSuggestionTriggerIndex(lastBrace);
          setActiveSuggestionIndex((prev) => Math.min(prev, matches.length - 1));
          return;
        }
      }
    }
    setShowSuggestions(false);
  };

  const insertPlaceholder = useCallback(
    (tag) => {
      const quill = quillRef.current?.getEditor();
      if (!quill || suggestionTriggerIndex === -1) return;

      const selection = quill.getSelection();
      const cursorIndex = selection ? selection.index : quill.getLength();
      const lengthToRemove = cursorIndex - suggestionTriggerIndex;

      quill.deleteText(suggestionTriggerIndex, lengthToRemove);
      quill.insertText(suggestionTriggerIndex, tag);
      quill.setSelection(suggestionTriggerIndex + tag.length);

      setShowSuggestions(false);
    },
    [suggestionTriggerIndex],
  );

  useEffect(() => {
    if (!editorContainer) return;
    const qlEditor = editorContainer.querySelector(".ql-editor");
    if (!qlEditor) return;

    const handleKeyDownCapture = (e) => {
      if (!showSuggestions || filteredPlaceholders.length === 0) return;

      if (e.key === "ArrowDown") {
        e.preventDefault();
        e.stopPropagation();
        setActiveSuggestionIndex((prev) => (prev + 1) % filteredPlaceholders.length);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        e.stopPropagation();
        setActiveSuggestionIndex(
          (prev) => (prev - 1 + filteredPlaceholders.length) % filteredPlaceholders.length,
        );
      } else if (e.key === "Enter") {
        e.preventDefault();
        e.stopPropagation();
        insertPlaceholder(filteredPlaceholders[activeSuggestionIndex].tag);
      } else if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        setShowSuggestions(false);
      }
    };

    qlEditor.addEventListener("keydown", handleKeyDownCapture, true);
    return () => {
      qlEditor.removeEventListener("keydown", handleKeyDownCapture, true);
    };
  }, [
    editorContainer,
    showSuggestions,
    filteredPlaceholders,
    activeSuggestionIndex,
    insertPlaceholder,
  ]);

  const userStaffRaw = localStorage.getItem("staffData");
  const userStaff = userStaffRaw ? JSON.parse(userStaffRaw) : null;
  const currentOrgName =
    localStorage.getItem("organization_name") ||
    userStaff?.organization?.organization_name ||
    "Fusion HR Solutions Pvt. Ltd.";
  const currentCenterName =
    sessionStorage.getItem("center_name") ||
    localStorage.getItem("center_name") ||
    userStaff?.primaryCenter?.center_name ||
    "Noida Office";

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingLogo(true);

    compressImage(file, 600, 600, (err, compressedBase64) => {
      if (!err && compressedBase64) {
        setLogoBase64(compressedBase64);
      } else {
        const reader = new FileReader();
        reader.onload = (event) => {
          setLogoBase64(event.target.result);
        };
        reader.readAsDataURL(file);
      }
    });

    const formData = new FormData();
    formData.append("files", file);
    formData.append("folderName", "staff");

    fileUploadApi(formData)
      .then((resp) => {
        if (checkStatusCodeSuccess(resp?.statusCode)) {
          const path = resp?.data[0];
          setLogoPath(path);
          toast.success("Logo uploaded successfully!");
        } else {
          toast.error(resp?.message || "Failed to upload logo.");
        }
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || err?.message);
      })
      .finally(() => {
        setUploadingLogo(false);
      });
  };

  const handleClearLogo = () => {
    setLogoPath("");
    setLogoBase64("");
    toast.info("Custom logo removed. Defaulting to favicon.");
  };

  const handleSignatureUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);

    compressImage(file, 400, 200, (err, compressedBase64) => {
      if (!err && compressedBase64) {
        setSignatureBase64(compressedBase64);
      } else {
        const reader = new FileReader();
        reader.onload = (event) => {
          setSignatureBase64(event.target.result);
        };
        reader.readAsDataURL(file);
      }
    });

    const formData = new FormData();
    formData.append("files", file);
    formData.append("folderName", "staff");

    fileUploadApi(formData)
      .then((resp) => {
        if (checkStatusCodeSuccess(resp?.statusCode)) {
          const path = resp?.data[0];
          setSignaturePath(path);
          toast.success("Signature uploaded successfully!");
        } else {
          toast.error(resp?.message || "Failed to upload signature.");
        }
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || err?.message);
      })
      .finally(() => {
        setUploading(false);
      });
  };

  const handleClearSignature = () => {
    setSignaturePath("");
    setSignatureBase64("");
    toast.info("Signature removed.");
  };

  const handleSave = () => {
    const payload = {
      settings: {
        include_logo: includeLogo,
        logo_path: logoPath || null,
        logo_width: Number(logoWidth) || 120,
        logo_align: logoAlign,
        include_signatory_signature: includeSignature,
        signatory_name: signatoryName,
        signatory_title: signatoryTitle,
        signature_path: signaturePath || null,
      },
      templates: templates,
    };

    setLoading(true);
    updateLetterConfigApi(payload)
      .then((resp) => {
        if (checkStatusCodeSuccess(resp?.statusCode)) {
          toast.success("Templates and signatory settings saved successfully!");
        } else {
          toast.error(resp?.message || "Failed to update configurations.");
        }
      })
      .catch((err) => {
        logError("Failed to save letter configurations", err);
        toast.error("Failed to save letter configurations.");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleResetToDefault = () => {
    const defaults = {
      offer: DEFAULT_OFFER_TEMPLATE,
      joining: DEFAULT_JOINING_TEMPLATE,
      training: DEFAULT_TRAINING_TEMPLATE,
      experience: DEFAULT_EXPERIENCE_TEMPLATE,
      relieving: DEFAULT_RELIEVING_TEMPLATE,
    };

    if (defaults[activeTab]) {
      setTemplates((prev) => ({
        ...prev,
        [activeTab]: defaults[activeTab],
      }));
      toast.info(
        `Reset ${activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} Letter template to default.`,
      );
    } else {
      setTemplates((prev) => ({
        ...prev,
        [activeTab]: "<p></p>",
      }));
      toast.info(`Cleared ${activeTab} template.`);
    }
  };

  const PREDEFINED_TABS = {
    offer: {
      label: "Offer Letter",
      icon: Briefcase,
      color: "#f59e0b",
      bg: "rgba(245, 158, 11, 0.08)",
    },
    joining: {
      label: "Joining Letter",
      icon: UserCheck,
      color: "#10b981",
      bg: "rgba(16, 185, 129, 0.08)",
    },
    training: {
      label: "Training Letter",
      icon: BookOpen,
      color: "#0ea5e9",
      bg: "rgba(14, 165, 233, 0.08)",
    },
    experience: {
      label: "Experience Letter",
      icon: Award,
      color: "#6366f1",
      bg: "rgba(99, 102, 241, 0.08)",
    },
    relieving: {
      label: "Relieving Letter",
      icon: FileMinus,
      color: "#f43f5e",
      bg: "rgba(244, 63, 94, 0.08)",
    },
  };

  const PREDEFINED_ORDER = ["offer", "joining", "training", "experience", "relieving"];
  const sortedKeys = Object.keys(templates).sort((a, b) => {
    const idxA = PREDEFINED_ORDER.indexOf(a);
    const idxB = PREDEFINED_ORDER.indexOf(b);
    if (idxA !== -1 && idxB !== -1) return idxA - idxB;
    if (idxA !== -1) return -1;
    if (idxB !== -1) return 1;
    return a.localeCompare(b);
  });

  const tabs = sortedKeys.map((key) => {
    if (PREDEFINED_TABS[key]) {
      return {
        key,
        ...PREDEFINED_TABS[key],
      };
    } else {
      const capitalizedLabel = key.charAt(0).toUpperCase() + key.slice(1);
      return {
        key,
        label: `${capitalizedLabel} Letter`,
        icon: FileText,
        color: "#8b5cf6",
        bg: "rgba(139, 92, 246, 0.08)",
      };
    }
  });

  return (
    <div>
      <style>{`
        .ql-container.ql-snow {
          border: 1px solid var(--glass-border) !important;
          border-bottom-left-radius: 12px;
          border-bottom-right-radius: 12px;
          background: var(--glass-bg);
          color: var(--text-primary);
          font-family: inherit;
        }
        .ql-toolbar.ql-snow {
          border: 1px solid var(--glass-border) !important;
          border-top-left-radius: 12px;
          border-top-right-radius: 12px;
          background: rgba(99,102,241,0.03);
          padding: 10px 15px !important;
        }
        .ql-editor {
          min-height: 220px !important;
          max-height: 400px !important;
          font-size: 0.875rem;
          line-height: 1.6;
        }
        .ql-editor.ql-blank::before {
          color: var(--text-muted) !important;
          font-style: normal !important;
        }
        .vertical-tabs-container {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        .vertical-tab-btn {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 12px 16px;
          border-radius: 12px;
          background: transparent;
          border: 1px solid transparent;
          color: var(--text-muted);
          font-weight: 500;
          font-size: 0.8125rem;
          cursor: pointer;
          text-align: left;
          transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
          width: 100%;
        }
        .vertical-tab-btn:hover {
          background: rgba(99, 102, 241, 0.04);
          color: var(--text-primary);
          transform: translateX(4px);
        }
        .vertical-tab-btn.active {
          background: rgba(99, 102, 241, 0.08) !important;
          border-color: rgba(99, 102, 241, 0.15) !important;
          color: var(--brand-indigo) !important;
          font-weight: 600 !important;
          box-shadow: 0 4px 12px rgba(99, 102, 241, 0.04);
        }
        .vertical-tab-btn.active:hover {
          transform: none;
        }
        .vertical-tab-btn .tab-icon-wrapper {
          transition: transform 0.2s ease-in-out;
        }
        .vertical-tab-btn:hover .tab-icon-wrapper {
          transform: scale(1.1);
        }
        @media (max-width: 767.98px) {
          .vertical-tabs-container {
            flex-direction: row !important;
            overflow-x: auto;
            padding-bottom: 8px;
            gap: 8px !important;
            scrollbar-width: thin;
          }
          .vertical-tab-btn {
            flex-shrink: 0;
            width: auto;
            justify-content: center;
            padding: 10px 14px;
          }
        }
        @keyframes shimmer {
          0% {
            background-position: -200% 0;
          }
          100% {
            background-position: 200% 0;
          }
        }
        .skeleton-shimmer {
          background: linear-gradient(90deg, rgba(99, 102, 241, 0.03) 25%, rgba(99, 102, 241, 0.08) 50%, rgba(99, 102, 241, 0.03) 75%);
          background-size: 200% 100%;
          animation: shimmer 1.5s infinite;
        }
      `}</style>
      <div className="container-fluid">
        {/* Page Header */}
        <div className="page-header dash-breadcrumb py-3">
          <div className="row align-items-center">
            <div className="col-6">
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div
                  style={{
                    width: 42,
                    height: 42,
                    borderRadius: 12,
                    background: "rgba(99,102,241,0.1)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <FileText size={20} style={{ color: "var(--brand-indigo)" }} />
                </div>
                <div>
                  <h4 className="f-w-600" style={{ margin: 0 }}>
                    Letter Templates
                  </h4>
                  <p
                    style={{
                      margin: 0,
                      fontSize: "0.75rem",
                      color: "var(--text-muted)",
                      marginTop: 2,
                    }}
                  >
                    Manage corporate template copies for staff documents
                  </p>
                </div>
              </div>
            </div>
            <div className="col-6 d-flex justify-content-end">
              <Link to="/setting">
                <button
                  type="button"
                  className="btn btn-pill"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "8px 16px",
                    borderRadius: 10,
                    background: "var(--icon-btn-bg)",
                    border: "1px solid var(--glass-border)",
                    color: "var(--text-primary)",
                    fontSize: "0.8125rem",
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  <ArrowLeft size={14} /> Back to Settings
                </button>
              </Link>
            </div>
          </div>
        </div>

        {/* Templates Page Content */}
        <div className="row mt-3">
          {/* Configuration Column */}
          <div className="col-12 col-xl-7 mb-4">
            {/* Editor Card */}
            <div
              className="card project-box mb-4"
              style={{
                background: "var(--glass-bg)",
                border: "1px solid var(--glass-border)",
                borderRadius: 16,
              }}
            >
              <div className="card-body">
                {isLoading ? (
                  <div className="d-flex flex-column flex-md-row gap-4 align-items-stretch">
                    {/* Left Sidebar Skeleton */}
                    <div style={{ flex: "0 0 240px" }} className="d-flex flex-column gap-3">
                      {[1, 2, 3, 4, 5].map((i) => (
                        <div
                          key={i}
                          className="skeleton-shimmer"
                          style={{ height: 48, borderRadius: 12 }}
                        />
                      ))}
                    </div>
                    {/* Right Editor Skeleton */}
                    <div style={{ flex: 1 }} className="d-flex flex-column gap-3">
                      <div
                        className="skeleton-shimmer"
                        style={{ height: 24, width: "150px", borderRadius: 6 }}
                      />
                      <div className="skeleton-shimmer" style={{ height: 260, borderRadius: 12 }} />
                      <div className="skeleton-shimmer" style={{ height: 120, borderRadius: 12 }} />
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="d-flex flex-column flex-md-row gap-4 align-items-stretch">
                      {/* Left Column: Sidebar with vertical tabs */}
                      <div style={{ flex: "0 0 240px" }}>
                        <div className="vertical-tabs-container">
                          {tabs.map((tab) => {
                            const Icon = tab.icon;
                            const isActive = activeTab === tab.key;
                            return (
                              <button
                                key={tab.key}
                                type="button"
                                onClick={() => setActiveTab(tab.key)}
                                className={`vertical-tab-btn ${isActive ? "active" : ""}`}
                              >
                                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                  <div
                                    className="tab-icon-wrapper"
                                    style={{
                                      width: 28,
                                      height: 28,
                                      borderRadius: 8,
                                      background: isActive ? tab.bg : "var(--glass-bg)",
                                      border: "1px solid",
                                      borderColor: isActive
                                        ? "rgba(99, 102, 241, 0.15)"
                                        : "var(--glass-border)",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                    }}
                                  >
                                    <Icon
                                      size={14}
                                      style={{ color: isActive ? tab.color : "var(--text-muted)" }}
                                    />
                                  </div>
                                  <span>{tab.label}</span>
                                </div>
                                {isActive && (
                                  <div
                                    style={{
                                      width: 6,
                                      height: 6,
                                      borderRadius: "50%",
                                      background: "var(--brand-indigo)",
                                      marginLeft: 8,
                                    }}
                                  />
                                )}
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {/* Right Column: Editor and Settings content */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        {/* Editor Textarea */}
                        <div>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              marginBottom: 8,
                            }}
                          >
                            <span
                              style={{
                                fontSize: "0.75rem",
                                fontWeight: 700,
                                color: "var(--text-muted)",
                                textTransform: "uppercase",
                              }}
                            >
                              Template Body Text
                            </span>
                            <button
                              type="button"
                              onClick={handleResetToDefault}
                              style={{
                                background: "transparent",
                                border: "none",
                                color: "var(--brand-indigo)",
                                fontSize: "0.75rem",
                                fontWeight: 600,
                                cursor: "pointer",
                              }}
                            >
                              Reset to Default
                            </button>
                          </div>
                          <ReactQuill
                            key={activeTab}
                            ref={setQuillRef}
                            theme="snow"
                            value={templates[activeTab] || ""}
                            onChange={(val) => {
                              setTemplates((prev) => ({
                                ...prev,
                                [activeTab]: val,
                              }));
                              const quill = quillRef.current?.getEditor();
                              if (quill) {
                                checkSuggestions(quill, quill.getSelection());
                              }
                            }}
                            onChangeSelection={(range) => {
                              const quill = quillRef.current?.getEditor();
                              if (quill) {
                                checkSuggestions(quill, range);
                              }
                            }}
                            style={{
                              background: "var(--glass-bg)",
                              color: "var(--text-primary)",
                              borderRadius: 8,
                            }}
                          />
                          {showSuggestions &&
                            filteredPlaceholders.length > 0 &&
                            editorContainer &&
                            createPortal(
                              <div
                                className="placeholder-suggestions-dropdown"
                                onMouseDown={(e) => e.stopPropagation()}
                                style={{
                                  position: "absolute",
                                  left: suggestionCoords.left,
                                  top: suggestionCoords.top,
                                  background: "var(--glass-bg)",
                                  border: "1px solid var(--glass-border)",
                                  borderRadius: "8px",
                                  boxShadow: "0 10px 25px rgba(0,0,0,0.2)",
                                  zIndex: 2000,
                                  minWidth: "220px",
                                  maxHeight: "180px",
                                  overflowY: "auto",
                                  padding: "4px 0",
                                  backdropFilter: "blur(12px)",
                                }}
                              >
                                {filteredPlaceholders.map((p, idx) => (
                                  <div
                                    key={p.tag}
                                    className={activeSuggestionIndex === idx ? "active-item" : ""}
                                    onMouseDown={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      insertPlaceholder(p.tag);
                                    }}
                                    onMouseEnter={() => setActiveSuggestionIndex(idx)}
                                    style={{
                                      padding: "8px 12px",
                                      fontSize: "0.8125rem",
                                      color:
                                        activeSuggestionIndex === idx
                                          ? "#fff"
                                          : "var(--text-primary)",
                                      background:
                                        activeSuggestionIndex === idx
                                          ? "var(--brand-indigo)"
                                          : "transparent",
                                      cursor: "pointer",
                                      fontWeight: 500,
                                      display: "flex",
                                      flexDirection: "column",
                                      gap: 2,
                                      transition: "background 0.15s, color 0.15s",
                                    }}
                                  >
                                    <span style={{ fontFamily: "monospace", fontWeight: 700 }}>
                                      {p.tag}
                                    </span>
                                    <span
                                      style={{
                                        fontSize: "0.6875rem",
                                        color:
                                          activeSuggestionIndex === idx
                                            ? "rgba(255,255,255,0.75)"
                                            : "var(--text-muted)",
                                      }}
                                    >
                                      {p.desc}
                                    </span>
                                  </div>
                                ))}
                              </div>,
                              editorContainer,
                            )}
                        </div>
                      </div>
                    </div>

                    {/* Branding & Signatory Settings Block */}
                    <div
                      style={{
                        marginTop: 24,
                        borderTop: "1px solid var(--glass-border)",
                        paddingTop: 20,
                      }}
                    >
                      <div
                        style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}
                      >
                        <FileText size={16} style={{ color: "var(--brand-indigo)" }} />
                        <h5 style={{ margin: 0, fontWeight: 700, fontSize: "0.9375rem" }}>
                          Branding & Signatory Settings
                        </h5>
                      </div>

                      <h6
                        style={{
                          fontSize: "0.8125rem",
                          fontWeight: 700,
                          color: "var(--brand-indigo)",
                          marginBottom: 12,
                        }}
                      >
                        1. Corporate Logo Settings
                      </h6>
                      <div className="row g-3 mb-4">
                        {/* Include Logo checkbox */}
                        <div className="col-12">
                          <label
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                              cursor: "pointer",
                              fontSize: "0.8125rem",
                              color: "var(--text-primary)",
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={includeLogo}
                              onChange={(e) => {
                                setIncludeLogo(e.target.checked);
                                localStorage.setItem("include_logo", String(e.target.checked));
                              }}
                              style={{
                                width: 16,
                                height: 16,
                                accentColor: "var(--brand-indigo)",
                              }}
                            />
                            Include Corporate Logo at Top of Letters
                          </label>
                        </div>

                        {/* Logo Image Upload */}
                        <div className="col-12 col-md-6">
                          <label
                            style={{
                              fontSize: "0.75rem",
                              fontWeight: 700,
                              color: "var(--text-muted)",
                              textTransform: "uppercase",
                              display: "block",
                              marginBottom: 6,
                            }}
                          >
                            Upload Logo Image
                          </label>
                          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                            <input
                              type="file"
                              accept="image/*"
                              id="logo-upload-input"
                              onChange={handleLogoUpload}
                              style={{ display: "none" }}
                            />
                            <button
                              type="button"
                              onClick={() => document.getElementById("logo-upload-input").click()}
                              className="btn btn-pill"
                              disabled={uploadingLogo}
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 6,
                                padding: "8px 16px",
                                borderRadius: 10,
                                background: "var(--brand-indigo)",
                                border: "1px solid var(--brand-indigo)",
                                color: "#fff",
                                fontSize: "0.8125rem",
                                fontWeight: 600,
                                cursor: "pointer",
                                flexShrink: 0,
                                whiteSpace: "nowrap",
                              }}
                            >
                              {uploadingLogo ? "Uploading..." : "Choose Logo"}
                            </button>

                            {logoPath && (
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 8,
                                  flexShrink: 0,
                                }}
                              >
                                <img
                                  src={getImageUrl(logoPath)}
                                  alt="Logo Preview"
                                  style={{
                                    maxHeight: "36px",
                                    border: "1px solid var(--glass-border)",
                                    borderRadius: 6,
                                    padding: 2,
                                    background: "#fff",
                                    flexShrink: 0,
                                  }}
                                />
                                <button
                                  type="button"
                                  onClick={handleClearLogo}
                                  style={{
                                    background: "transparent",
                                    border: "none",
                                    color: "var(--danger)",
                                    fontSize: "0.75rem",
                                    fontWeight: 600,
                                    cursor: "pointer",
                                    flexShrink: 0,
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  Remove
                                </button>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Logo Size Input */}
                        <div className="col-6 col-md-3">
                          <label
                            style={{
                              fontSize: "0.75rem",
                              fontWeight: 700,
                              color: "var(--text-muted)",
                              textTransform: "uppercase",
                              display: "block",
                              marginBottom: 6,
                            }}
                          >
                            Logo Width (px)
                          </label>
                          <input
                            type="number"
                            className="form-control"
                            value={logoWidth}
                            onChange={(e) => {
                              setLogoWidth(e.target.value);
                              localStorage.setItem("logo_width", e.target.value);
                            }}
                            min="20"
                            max="300"
                            style={{
                              background: "var(--glass-bg)",
                              color: "var(--text-primary)",
                              border: "1px solid var(--glass-border)",
                              borderRadius: 10,
                              fontSize: "0.8125rem",
                              padding: "10px 14px",
                            }}
                          />
                        </div>

                        {/* Logo Alignment */}
                        <div className="col-6 col-md-3">
                          <label
                            style={{
                              fontSize: "0.75rem",
                              fontWeight: 700,
                              color: "var(--text-muted)",
                              textTransform: "uppercase",
                              display: "block",
                              marginBottom: 6,
                            }}
                          >
                            Logo Alignment
                          </label>
                          <select
                            className="form-select"
                            value={logoAlign}
                            onChange={(e) => {
                              setLogoAlign(e.target.value);
                              localStorage.setItem("logo_align", e.target.value);
                            }}
                            style={{
                              background: "var(--glass-bg)",
                              color: "var(--text-primary)",
                              border: "1px solid var(--glass-border)",
                              borderRadius: 10,
                              fontSize: "0.8125rem",
                              padding: "10px 14px",
                            }}
                          >
                            <option value="left">Left Align</option>
                            <option value="center">Center Align</option>
                            <option value="right">Right Align</option>
                          </select>
                        </div>
                      </div>

                      <h6
                        style={{
                          fontSize: "0.8125rem",
                          fontWeight: 700,
                          color: "var(--brand-indigo)",
                          marginBottom: 12,
                          borderTop: "1px dashed var(--glass-border)",
                          paddingTop: 16,
                        }}
                      >
                        2. Authorized Signatory Settings
                      </h6>
                      <div className="row g-3">
                        {/* Checkbox to include/exclude */}
                        <div className="col-12">
                          <label
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                              cursor: "pointer",
                              fontSize: "0.8125rem",
                              color: "var(--text-primary)",
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={includeSignature}
                              onChange={(e) => {
                                setIncludeSignature(e.target.checked);
                                localStorage.setItem(
                                  "include_signatory_signature",
                                  String(e.target.checked),
                                );
                              }}
                              style={{
                                width: 16,
                                height: 16,
                                accentColor: "var(--brand-indigo)",
                              }}
                            />
                            Include Digital Signature & Stamp in Letters
                          </label>
                        </div>

                        {/* Signatory Name */}
                        <div className="col-12 col-sm-6">
                          <label
                            style={{
                              fontSize: "0.75rem",
                              fontWeight: 700,
                              color: "var(--text-muted)",
                              textTransform: "uppercase",
                              display: "block",
                              marginBottom: 6,
                            }}
                          >
                            Signatory Name
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            value={signatoryName}
                            onChange={(e) => {
                              setSignatoryName(e.target.value);
                              localStorage.setItem("signatory_name", e.target.value);
                            }}
                            placeholder="e.g. Abhishek Sharma"
                            style={{
                              background: "var(--glass-bg)",
                              color: "var(--text-primary)",
                              border: "1px solid var(--glass-border)",
                              borderRadius: 10,
                              fontSize: "0.8125rem",
                              padding: "10px 14px",
                            }}
                          />
                        </div>

                        {/* Signatory Designation */}
                        <div className="col-12 col-sm-6">
                          <label
                            style={{
                              fontSize: "0.75rem",
                              fontWeight: 700,
                              color: "var(--text-muted)",
                              textTransform: "uppercase",
                              display: "block",
                              marginBottom: 6,
                            }}
                          >
                            Designation / Title
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            value={signatoryTitle}
                            onChange={(e) => {
                              setSignatoryTitle(e.target.value);
                              localStorage.setItem("signatory_title", e.target.value);
                            }}
                            placeholder="e.g. Authorized Signatory"
                            style={{
                              background: "var(--glass-bg)",
                              color: "var(--text-primary)",
                              border: "1px solid var(--glass-border)",
                              borderRadius: 10,
                              fontSize: "0.8125rem",
                              padding: "10px 14px",
                            }}
                          />
                        </div>

                        {/* Signature Upload */}
                        <div className="col-12">
                          <label
                            style={{
                              fontSize: "0.75rem",
                              fontWeight: 700,
                              color: "var(--text-muted)",
                              textTransform: "uppercase",
                              display: "block",
                              marginBottom: 6,
                            }}
                          >
                            Upload Signature Image (.png, .jpg)
                          </label>
                          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                            <input
                              type="file"
                              accept="image/*"
                              id="signature-upload-input"
                              onChange={handleSignatureUpload}
                              style={{ display: "none" }}
                            />
                            <button
                              type="button"
                              onClick={() =>
                                document.getElementById("signature-upload-input").click()
                              }
                              className="btn btn-pill"
                              disabled={uploading}
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 6,
                                padding: "8px 16px",
                                borderRadius: 10,
                                background: "var(--brand-indigo)",
                                border: "1px solid var(--brand-indigo)",
                                color: "#fff",
                                fontSize: "0.8125rem",
                                fontWeight: 600,
                                cursor: "pointer",
                              }}
                            >
                              {uploading ? "Uploading..." : "Choose File"}
                            </button>

                            {signaturePath && (
                              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <img
                                  src={getImageUrl(signaturePath)}
                                  alt="Signature Preview"
                                  style={{
                                    maxHeight: "36px",
                                    border: "1px solid var(--glass-border)",
                                    borderRadius: 6,
                                    padding: 2,
                                    background: "#fff",
                                  }}
                                />
                                <button
                                  type="button"
                                  onClick={handleClearSignature}
                                  style={{
                                    background: "transparent",
                                    border: "none",
                                    color: "var(--danger)",
                                    fontSize: "0.75rem",
                                    fontWeight: 600,
                                    cursor: "pointer",
                                  }}
                                >
                                  Remove
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Card Actions */}
                    <div
                      style={{
                        borderTop: "1px solid var(--glass-border)",
                        marginTop: 20,
                        paddingTop: 16,
                        display: "flex",
                        justifyContent: "flex-end",
                      }}
                    >
                      <BaseButton
                        color="primary"
                        onClick={handleSave}
                        style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
                      >
                        <Save size={14} /> Save Templates
                      </BaseButton>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Sidebar / Instructions Column placed underneath the editor */}
            <div
              className="card project-box"
              style={{
                background: "var(--glass-bg)",
                border: "1px solid var(--glass-border)",
                borderRadius: 16,
              }}
            >
              <div className="card-body">
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
                  <Info size={16} style={{ color: "var(--brand-indigo)" }} />
                  <h5 style={{ margin: 0, fontWeight: 700, fontSize: "0.9375rem" }}>
                    Template Variables
                  </h5>
                </div>
                <p
                  style={{
                    fontSize: "0.75rem",
                    color: "var(--text-muted)",
                    lineHeight: 1.5,
                    marginBottom: 16,
                  }}
                >
                  Use these placeholder tags in your templates. The system will automatically swap
                  them out for verified staff information when generating letters.
                </p>

                {/* Placeholders list */}
                <div className="row g-2">
                  {placeholders.map((p, i) => (
                    <div key={i} className="col-12 col-sm-6 col-md-4">
                      <div
                        style={{
                          padding: "10px 12px",
                          borderRadius: 10,
                          background: "rgba(99,102,241,0.04)",
                          border: "1px solid var(--glass-border)",
                          height: "100%",
                        }}
                      >
                        <div
                          style={{
                            fontFamily: "monospace",
                            fontWeight: 700,
                            color: "var(--brand-indigo)",
                            fontSize: "0.75rem",
                            marginBottom: 3,
                          }}
                        >
                          {p.tag}
                        </div>
                        <div style={{ fontSize: "0.6875rem", color: "var(--text-muted)" }}>
                          {p.desc}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Interactive Live Preview Column */}
          <div className="col-12 col-xl-5 mb-4">
            <div
              className="card project-box"
              style={{
                background: "var(--glass-bg)",
                border: "1px solid var(--glass-border)",
                borderRadius: 16,
                height: "100%",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <div
                className="card-body"
                style={{ display: "flex", flexDirection: "column", height: "100%" }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                  <Eye size={16} style={{ color: "var(--brand-indigo)" }} />
                  <h5 style={{ margin: 0, fontWeight: 700, fontSize: "0.9375rem" }}>
                    Live Document Preview
                  </h5>
                </div>
                <p
                  style={{
                    fontSize: "0.75rem",
                    color: "var(--text-muted)",
                    lineHeight: 1.5,
                    marginBottom: 16,
                  }}
                >
                  See dynamic changes reflected in real-time below using realistic placeholder data.
                </p>

                <div
                  style={{
                    background: "rgba(99, 102, 241, 0.03)",
                    border: "1px dashed var(--glass-border)",
                    borderRadius: 12,
                    padding: "16px",
                    flexGrow: 1,
                    overflowY: "auto",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "flex-start",
                    minHeight: "450px",
                  }}
                >
                  {isLoading ? (
                    <div
                      style={{ width: "100%", maxWidth: "460px" }}
                      className="d-flex flex-column gap-3"
                    >
                      <div className="skeleton-shimmer" style={{ height: 40, borderRadius: 8 }} />
                      <div
                        className="skeleton-shimmer"
                        style={{ height: 24, width: "60%", borderRadius: 6 }}
                      />
                      <div className="skeleton-shimmer" style={{ height: 200, borderRadius: 8 }} />
                      <div className="skeleton-shimmer" style={{ height: 60, borderRadius: 8 }} />
                    </div>
                  ) : (
                    /* Mini A4 Sheet */
                    <div
                      style={{
                        background: "#fff",
                        color: "#222",
                        width: "100%",
                        maxWidth: "460px",
                        padding: "clamp(12px, 4vw, 30px) clamp(10px, 3vw, 24px)",
                        borderRadius: 8,
                        boxShadow: "0 8px 30px rgba(0,0,0,0.06)",
                        fontFamily: "'Inter', sans-serif",
                        fontSize: "0.8125rem",
                        lineHeight: "1.5",
                        textAlign: "left",
                        border: "1px solid #eaeaea",
                      }}
                    >
                      {/* Letterhead */}
                      <div
                        style={{
                          borderBottom: "1.5px solid #222",
                          paddingBottom: "8px",
                          marginBottom: "16px",
                          display: "flex",
                          flexDirection:
                            logoAlign === "center"
                              ? "column"
                              : logoAlign === "left"
                                ? "row"
                                : "row-reverse",
                          justifyContent: "space-between",
                          alignItems: "center",
                          gap: "12px",
                        }}
                      >
                        {/* Logo */}
                        {includeLogo && (
                          <div>
                            <img
                              src={
                                logoBase64
                                  ? logoBase64
                                  : logoPath && logoPath !== "logo.png" && logoPath !== "/logo.png"
                                    ? getImageUrl(logoPath)
                                    : logoLight
                              }
                              alt="Logo"
                              style={{
                                width: `${logoWidth}px`,
                                height: "auto",
                                display: "block",
                              }}
                            />
                          </div>
                        )}

                        {/* Organization Details */}
                        <div
                          style={{
                            textAlign:
                              logoAlign === "center"
                                ? "center"
                                : logoAlign === "left"
                                  ? "right"
                                  : "left",
                          }}
                        >
                          <h6
                            style={{
                              margin: 0,
                              fontWeight: 800,
                              color: "#111",
                              fontSize: "0.95rem",
                            }}
                          >
                            {currentOrgName.toUpperCase()}
                          </h6>
                          <span style={{ fontSize: "0.65rem", color: "#666", fontWeight: 500 }}>
                            {currentCenterName}
                          </span>
                        </div>
                      </div>

                      {/* Content */}
                      <div
                        className="letter-preview-content"
                        style={{ minHeight: "220px", color: "#333" }}
                        dangerouslySetInnerHTML={{
                          __html: resolvePlaceholders(
                            templates[activeTab] || "",
                            currentOrgName,
                            currentCenterName,
                          ),
                        }}
                      />

                      {/* Signature */}
                      <div
                        style={{
                          marginTop: "24px",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-end",
                        }}
                      >
                        <div>
                          <span style={{ fontSize: "0.65rem", color: "#666" }}>Prepared By</span>
                          <div
                            style={{
                              fontWeight: 600,
                              color: "#222",
                              fontSize: "0.75rem",
                              marginTop: 2,
                            }}
                          >
                            HR Fusions
                          </div>
                        </div>
                        <div style={{ position: "relative", textAlign: "right" }}>
                          {includeSignature && (
                            <>
                              {/* Digital stamp badge */}
                              <div
                                style={{
                                  position: "absolute",
                                  right: "50px",
                                  bottom: "15px",
                                  width: "60px",
                                  height: "60px",
                                  borderRadius: "50%",
                                  border: "2px dashed rgba(99,102,241,0.3)",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontSize: "0.45rem",
                                  color: "rgba(99,102,241,0.5)",
                                  fontWeight: 800,
                                  textTransform: "uppercase",
                                  transform: "rotate(-15deg)",
                                  pointerEvents: "none",
                                }}
                              >
                                Approved
                              </div>

                              {/* Signature Image or Stylized Name */}
                              {signatureBase64 ? (
                                <img
                                  src={signatureBase64}
                                  alt="Signature"
                                  style={{
                                    maxHeight: "36px",
                                    display: "block",
                                    marginLeft: "auto",
                                    marginBottom: "4px",
                                  }}
                                />
                              ) : signaturePath ? (
                                <img
                                  src={getImageUrl(signaturePath)}
                                  alt="Signature"
                                  style={{
                                    maxHeight: "36px",
                                    display: "block",
                                    marginLeft: "auto",
                                    marginBottom: "4px",
                                  }}
                                />
                              ) : (
                                <div
                                  style={{
                                    fontFamily: "'Courier New', Courier, monospace",
                                    fontStyle: "italic",
                                    fontSize: "0.95rem",
                                    fontWeight: 700,
                                    color: "#1e3a8a",
                                    transform: "rotate(-5deg)",
                                    marginBottom: "4px",
                                  }}
                                >
                                  {signatoryName}
                                </div>
                              )}
                            </>
                          )}

                          {!includeSignature && <div style={{ height: "36px" }}></div>}

                          <span style={{ fontSize: "0.65rem", color: "#666" }}>
                            For {currentOrgName}
                          </span>
                          <div
                            style={{
                              fontWeight: 700,
                              color: "#222",
                              fontSize: "0.75rem",
                              marginTop: 2,
                            }}
                          >
                            {signatoryTitle}
                          </div>
                          {includeSignature && (
                            <span style={{ fontSize: "0.65rem", color: "#666" }}>
                              ({signatoryName})
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LetterTemplates;
